import type { PaymentCase } from '../PaymentDetailModal/PaymentDetailModal.types'
import { VirtualCardType } from 'modules/virtual-cards/constants'

// Re-export AvailableVcnCard from API for backward compatibility
export type { AvailableVcnCard } from 'enrollment-platform/api/vcn'

export interface VirtualCardData {
  pan?: string
  cvv_number?: string
  expiration_time?: string
  firstName?: string
  lastName?: string
  state?: string
  status?: string
  token?: string
  merchantLogId?: string
  monthly_card_limit?: number | '-'
  created_time?: string
  last_modified_time?: string
  vcnProvider?: string
  cardholderName?: string
  card_public_key?: string
}

/** Card actions that can be performed on an existing VCN */
export enum VcnCardAction {
  SUSPEND = 'suspend',
  TERMINATE = 'terminate',
  REACTIVATE = 'reactivate',
}

/** All VCN actions including generate */
export enum VcnActionType {
  SUSPEND = 'suspend',
  TERMINATE = 'terminate',
  REACTIVATE = 'reactivate',
  GENERATE = 'generate',
}

/** Union type for VCN actions (for backward compatibility) */
export type VcnAction = `${VcnActionType}`

/** Labels for VCN card actions */
export const VCN_CARD_ACTION_LABELS: Record<VcnCardAction, string> = {
  [VcnCardAction.SUSPEND]: 'suspend',
  [VcnCardAction.TERMINATE]: 'terminate',
  [VcnCardAction.REACTIVATE]: 'reactivate',
} as const

export interface VcnActionOption {
  id: VcnAction
  name: string
  visible: string | string[]
}

export interface VcnPaymentManagementProps {
  paymentCase: PaymentCase
  onCopy: (text: string) => void
  canUpdateCase?: boolean
  onComplete?: () => void
}

export interface VcnDetailsTableProps {
  virtualCard: VirtualCardData
  onCopy: (text: string) => void
}

export interface VcnActionsSelectProps {
  cardState: string
  value?: VcnAction
  onActionSelect: (action: VcnAction | undefined) => void
  disabled?: boolean
  loading?: boolean
}

export interface GenerateVcnModalProps {
  open: boolean
  onCancel: () => void
  onConfirm: (reason: string, provider: VirtualCardType) => void
  /** Called when using activity log popup flow (flag1 enabled) */
  onConfirmWithActivityLog?: (
    provider: VirtualCardType,
    options: {
      reason?: string
      reasonCategory?: string
      zendeskTicket?: string
    }
  ) => void
  loading?: boolean
  enabledProviders: VirtualCardType[]
  /** Show termination warning - should be true only when terminate-all flag is enabled AND existing cards exist */
  showTerminateWarning?: boolean
  /** Pre-selected provider from dropdown menu */
  selectedProvider?: VirtualCardType | null
  /** Show activity log popup instead of simple reason input */
  useActivityLogPopup?: boolean
  /** Person full name for modal title */
  personFullName?: string
}

export interface UseVcnPaymentManagementOptions {
  paymentCase: PaymentCase
  onComplete?: () => void
}

export interface UseVcnActionsOptions {
  benefitId: string
  employeePublicKey?: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export interface ReuseVcnModalProps {
  open: boolean
  onCancel: () => void
  /** Called for simple confirmation (no activity log) */
  onConfirm: () => void
  /** Called when using activity log popup flow */
  onConfirmWithActivityLog?: (options: {
    reason?: string
    reasonCategory?: string
    zendeskTicket?: string
  }) => void
  loading?: boolean
  /** Show activity log form instead of simple confirmation */
  useActivityLogPopup?: boolean
  /** Card PAN to display */
  cardPan?: string
  /** Person full name for modal description */
  personFullName?: string
}
